
import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  return <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background with overlay */}
      <div className="absolute inset-0 bg-knightblack/95 z-0"></div>
      
      {/* Gold accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-goldcore z-10"></div>
      
      {/* Chess board pattern - subtle background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjMTExMTExIiBmaWxsLW9wYWNpdHk9IjAuMSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMCAwaDIwdjIwSDB6Ii8+PHBhdGggZD0iTTIwIDIwaDIwdjIwSDIweiIvPjwvZz48L3N2Zz4=')] opacity-5 z-0"></div>
      
      <div className="container mx-auto px-4 py-12 sm:py-16 lg:py-24 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left Column - Main Content */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight text-softwhite">
              <span className="text-goldcore">Strategic Marketing</span> For Your Business Growth
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-softwhite/90">Make your next move count.</p>
            
            {/* Feature Points */}
            <div className="flex flex-col space-y-4 mb-8 text-left max-w-md mx-auto lg:mx-0">
              <div className="flex items-center space-x-3">
                <Zap className="h-6 w-6 text-goldcore" />
                <span className="text-softwhite">AI-powered process</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-goldcore" />
                <span className="text-softwhite">Free site builds for qualified businesses</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              {/* Main CTA Button */}
              <Button variant="gold" size="default" className="group" asChild>
                <Link to="/chat">
                  Chat with KnightAI
                  <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              
              {/* Secondary CTA Button */}
              <Button variant="dark" size="default">
                See Our Work
              </Button>
            </div>
          </div>
          
          {/* Right Column - Knight AI Chat Preview */}
          <div className="lg:w-1/2 relative">
            <div className="bg-deepshadow/60 backdrop-blur-lg rounded-xl p-4 shadow-xl border border-goldcore/20 w-full max-w-md mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-goldcore/20 flex items-center justify-center">
                    <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
                  </div>
                  <span className="font-bold text-softwhite">KnightAI</span>
                </div>
                <span className="text-xs text-softwhite/60">Online</span>
              </div>
              
              <div className="space-y-4 max-h-80 overflow-y-auto mb-4">
                {/* AI Message */}
                <div className="flex gap-2">
                  <div className="h-8 w-8 rounded-full bg-goldcore/20 flex-shrink-0 flex items-center justify-center">
                    <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
                  </div>
                  <div className="bg-deepshadow/70 rounded-lg rounded-tl-none p-3 text-softwhite max-w-[80%]">
                    <p>Hi there! I'm KnightAI, your strategic growth partner. Tell me about your business, and I'll help seize its best interest.</p>
                  </div>
                </div>
                
                {/* User Message */}
                <div className="flex gap-2 justify-end">
                  <div className="bg-tacticalblue/20 rounded-lg rounded-tr-none p-3 text-softwhite max-w-[80%]">
                    <p>I run a small coffee shop and need to take online orders. Can you help?</p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-tacticalblue/30 flex-shrink-0 flex items-center justify-center">
                    <span className="text-softwhite text-xs">You</span>
                  </div>
                </div>
                
                {/* AI Message */}
                <div className="flex gap-2">
                  <div className="h-8 w-8 rounded-full bg-goldcore/20 flex-shrink-0 flex items-center justify-center">
                    <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
                  </div>
                  <div className="bg-deepshadow/70 rounded-lg rounded-tl-none p-3 text-softwhite max-w-[80%]">
                    <p>Perfect! For your coffee shop, I'll design a site with online ordering, your menu, and a loyalty program. What's your shop's name and style?</p>
                  </div>
                </div>
                
                {/* Typing Indicator */}
                <div className="flex gap-2">
                  
                  
                </div>
              </div>
              
              {/* Chat Input */}
              <div className="relative">
                <input type="text" placeholder="Type your message..." className="w-full bg-softwhite/10 border border-softwhite/20 rounded-lg py-3 px-4 text-softwhite focus:outline-none focus:ring-2 focus:ring-goldcore/50 focus:border-transparent" />
                <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-goldcore hover:text-goldcore/80">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Decorative element */}
            <div className="absolute -bottom-10 -right-10 h-40 w-40 bg-goldcore/10 rounded-full blur-3xl"></div>
            <div className="absolute -top-10 -left-10 h-24 w-24 bg-tacticalblue/10 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </div>;
};
export default HeroSection;
